import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Trash2, ChevronUp, ChevronDown, User, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { LugarAutocomplete } from './LugarAutocomplete';

/**
 * Tarjeta de un pasajero (nombre, teléfono, correo, origen, destino).
 * Reusable en SolicitarViajePage y en el modal de edición del Calendario.
 *
 * Props:
 *  - pasajero: { nombre, telefono, correo, origen, destino }
 *  - index: number
 *  - total: number
 *  - onUpdate(index, pasajero)
 *  - onRemove(index)
 *  - onMoveUp(index)
 *  - onMoveDown(index)
 *  - origenes, destinos: opcional, sugerencias para datalist
 */
export const PasajeroItem = ({ pasajero, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, origenes = [], destinos = [] }) => {
  const [sugerencias, setSugerencias] = useState([]);
  const [showSugerencias, setShowSugerencias] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const inputRef = useRef(null);
  const sugerenciasRef = useRef(null);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sugerenciasRef.current && !sugerenciasRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buscarSugerencias = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSugerencias([]);
      return;
    }
    try {
      const response = await api.get('/servicios/autocomplete-pasajeros', {
        params: { q: query }
      });
      setSugerencias(response.data || []);
      setShowSugerencias(true);
    } catch (error) {
      console.error('Error buscando sugerencias:', error);
    }
  }, []);

  const handleNombreChange = (value) => {
    onUpdate(index, { ...pasajero, nombre: value });
    if (searchTimeout) clearTimeout(searchTimeout);
    const t = setTimeout(() => buscarSugerencias(value), 300);
    setSearchTimeout(t);
  };

  const seleccionarSugerencia = (sug) => {
    onUpdate(index, {
      ...pasajero,
      nombre: sug.nombre,
      telefono: sug.telefono,
      correo: sug.correo,
      origen: pasajero.origen?.trim() ? pasajero.origen : (sug.origen || ''),
      destino: pasajero.destino?.trim() ? pasajero.destino : (sug.destino || '')
    });
    setShowSugerencias(false);
    setSugerencias([]);
    const extra = [];
    if (sug.origen && !pasajero.origen?.trim()) extra.push('origen');
    if (sug.destino && !pasajero.destino?.trim()) extra.push('destino');
    toast.success(extra.length > 0
      ? `Datos del pasajero autocompletados (incluye ${extra.join(' y ')})`
      : 'Datos del pasajero autocompletados');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-bold text-primary">{index + 1}</span>
          </div>
          <span className="font-medium text-gray-700">Pasajero {index + 1}</span>
          {index === 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Primera recogida</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className="h-8 w-8"
            title="Mover arriba"
          >
            <ChevronUp size={16} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            className="h-8 w-8"
            title="Mover abajo"
          >
            <ChevronDown size={16} />
          </Button>
          {total > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
              title="Eliminar pasajero"
            >
              <Trash2 size={16} />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Label className="text-xs text-gray-500">Nombre</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              ref={inputRef}
              value={pasajero.nombre}
              onChange={(e) => handleNombreChange(e.target.value)}
              onFocus={() => pasajero.nombre.length >= 2 && sugerencias.length > 0 && setShowSugerencias(true)}
              className="pl-9 h-9"
              placeholder="Nombre del pasajero"
              data-testid={`pasajero-${index}-nombre`}
              autoComplete="off"
            />
            {showSugerencias && sugerencias.length > 0 && (
              <div
                ref={sugerenciasRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
              >
                {sugerencias.map((sug, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); seleccionarSugerencia(sug); }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 flex flex-col border-b last:border-b-0"
                  >
                    <span className="font-medium text-gray-900">{sug.nombre}</span>
                    <span className="text-xs text-gray-500">
                      {[sug.telefono, sug.correo].filter(Boolean).join(' • ') || 'Sin contacto'}
                      {sug.origen && sug.destino && (
                        <span className="ml-1">— último: {sug.origen} → {sug.destino}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Teléfono</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              value={pasajero.telefono}
              onChange={(e) => onUpdate(index, { ...pasajero, telefono: e.target.value })}
              className="pl-9 h-9"
              placeholder="+56 9 1234 5678"
              data-testid={`pasajero-${index}-telefono`}
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Correo</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              type="email"
              value={pasajero.correo}
              onChange={(e) => onUpdate(index, { ...pasajero, correo: e.target.value })}
              className="pl-9 h-9"
              placeholder="correo@empresa.com"
              data-testid={`pasajero-${index}-correo`}
            />
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <div>
            <Label className="text-xs text-gray-500">Origen</Label>
            <LugarAutocomplete
              tipo="origen"
              value={pasajero.origen}
              onChange={(v) => onUpdate(index, { ...pasajero, origen: v })}
              placeholder="Dirección de recogida"
              iconColor="text-green-600"
              testId={`pasajero-${index}-origen`}
            />
          </div>

          <button
            type="button"
            onClick={() => onUpdate(index, { ...pasajero, origen: pasajero.destino, destino: pasajero.origen })}
            disabled={!pasajero.origen && !pasajero.destino}
            className="h-9 w-9 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-primary hover:text-white hover:border-primary text-gray-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Intercambiar origen y destino"
            data-testid={`pasajero-${index}-swap`}
          >
            <ArrowLeftRight size={16} />
          </button>

          <div>
            <Label className="text-xs text-gray-500">Destino</Label>
            <LugarAutocomplete
              tipo="destino"
              value={pasajero.destino}
              onChange={(v) => onUpdate(index, { ...pasajero, destino: v })}
              placeholder="Dirección de destino"
              iconColor="text-red-600"
              testId={`pasajero-${index}-destino`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasajeroItem;
